import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { DateFormatProvider } from './contexts/DateFormatContext';
import './i18n';
import LoginPage from './components/LoginPage';
import AuthCallback from './components/AuthCallback';
import Header from './components/Header';
import TransactionForm from './components/TransactionForm';
import TransactionList from './components/TransactionList';
import MonthlySummary from './components/MonthlySummary';
import CSVImport from './components/CSVImport';
import CategoryManagement from './components/CategoryManagement';
import SettlementManagement from './components/SettlementManagement';
import BudgetManagement from './components/BudgetManagement';
import BudgetProgressOverview from './components/BudgetProgressOverview';
import './App.css';

const App: React.FC = () => {
  return (
    <AuthProvider>
      <CurrencyProvider>
        <DateFormatProvider>
          <Router>
            <AppContent />
          </Router>
        </DateFormatProvider>
      </CurrencyProvider>
    </AuthProvider>
  );
};

const AppContent: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem',
        color: '#666'
      }}>
        {t('common:loading')}
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <div className="App">
      <Header />
      <nav className="app-nav">
        <Link to="/" className="nav-link" title={t('common:home')}>
          <span className="nav-icon">🏠</span>
          <span className="nav-text">{t('common:home')}</span>
        </Link>
        <Link to="/add" className="nav-link" title={t('transaction:addTransaction')}>
          <span className="nav-icon">➕</span>
          <span className="nav-text">{t('transaction:addTransaction')}</span>
        </Link>
        <Link to="/import" className="nav-link" title={t('transaction:csvImport')}>
          <span className="nav-icon">📤</span>
          <span className="nav-text">{t('transaction:csvImport')}</span>
        </Link>
        <Link to="/list" className="nav-link" title={t('transaction:transactionList')}>
          <span className="nav-icon">📋</span>
          <span className="nav-text">{t('transaction:transactionList')}</span>
        </Link>
        <Link to="/summary" className="nav-link" title={t('common:monthlySummary')}>
          <span className="nav-icon">📊</span>
          <span className="nav-text">{t('common:monthlySummary')}</span>
        </Link>
        <Link to="/categories" className="nav-link" title={t('category:categoryManagement')}>
          <span className="nav-icon">🏷️</span>
          <span className="nav-text">{t('category:categoryManagement')}</span>
        </Link>
        <Link to="/budgets" className="nav-link" title={t('budget:budgetManagement')}>
          <span className="nav-icon">💰</span>
          <span className="nav-text">{t('budget:budgetManagement')}</span>
        </Link>
        <Link to="/settlements" className="nav-link" title={t('settlement:settlementManagement')}>
          <span className="nav-icon">⚖️</span>
          <span className="nav-text">{t('settlement:settlementManagement')}</span>
        </Link>
      </nav>
      
      <main className="App-main">
        <Routes>
          <Route path="/" element={<MobileHome />} />
          <Route path="/add" element={<TransactionForm />} />
          <Route path="/import" element={<CSVImportPage />} />
          <Route path="/list" element={<TransactionList />} />
          <Route path="/summary" element={<MonthlySummary />} />
          <Route path="/categories" element={<CategoryManagement />} />
          <Route path="/budgets" element={<BudgetManagement />} />
          <Route path="/settlements" element={<SettlementPage />} />
        </Routes>
      </main>
    </div>
  );
};

const MobileHome: React.FC = () => {
  // モバイル判定
  const isMobile = window.innerWidth <= 768;
  
  if (isMobile) {
    // モバイルでは直接Transaction追加画面を表示
    return <TransactionForm />;
  }
  
  // デスクトップでは従来のHome画面を表示
  return <Home />;
};

const Home: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="home">
      <h2>{t('auth:welcome')}！</h2>
      <p>収入と支出を記録して、家計を管理しましょう。</p>
      
      <div style={{ marginBottom: '2rem' }}>
        <BudgetProgressOverview />
      </div>
      
      <div className="quick-actions">
        <Link to="/add" className="btn btn-primary">{t('transaction:addTransaction')}</Link>
        <Link to="/import" className="btn btn-secondary">{t('transaction:csvImport')}</Link>
        <Link to="/list" className="btn btn-secondary">{t('transaction:transactionList')}</Link>
        <Link to="/budgets" className="btn btn-secondary">{t('budget:budgetManagement')}</Link>
        <Link to="/settlements" className="btn btn-secondary">{t('settlement:settlementManagement')}</Link>
      </div>
    </div>
  );
};

const CSVImportPage: React.FC = () => {
  const { t } = useTranslation();
  
  const handleImportComplete = () => {
    // インポート完了後の処理（必要に応じて）
    console.log('CSVインポートが完了しました');
  };

  return (
    <div>
      <CSVImport onImportComplete={handleImportComplete} />
    </div>
  );
};

const SettlementPage: React.FC = () => {
  const handleTransactionUpdate = () => {
    // 必要に応じて他のコンポーネントに更新を通知
    console.log('精算処理が完了しました');
  };

  return (
    <div>
      <SettlementManagement onTransactionUpdate={handleTransactionUpdate} />
    </div>
  );
};

export default App;